import { createFileRoute, Link, notFound, useRouter } from "@tanstack/react-router";
import { Calendar, Clock, ArrowLeft, MessageCircle, ChevronRight } from "lucide-react";
import { BLOG_POSTS, POSTS_BY_SLUG, type BlogPost } from "@/lib/blog-posts";
import { KENTRO } from "@/lib/constants";
import { BlogNotFound, BlogErrorState } from "@/components/blog/BlogNotFound";

const SITE = "https://kentro.com.br";

export const Route = createFileRoute("/blog/$slug")({
  loader: ({ params }) => {
    const post = POSTS_BY_SLUG[params.slug];
    if (!post) throw notFound();
    return { slug: post.slug };
  },
  head: ({ params }) => {
    const post = POSTS_BY_SLUG[params.slug];
    if (!post) return { meta: [{ title: "Post — Kentro" }] };
    const url = `${SITE}/blog/${params.slug}`;
    const articleImage = post.cover ? `${SITE}${post.cover}` : `${SITE}/og-image.png`;
    const articleLd = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "@id": `${url}#article`,
      url,
      headline: post.title,
      name: post.title,
      description: post.description,
      image: [articleImage],
      datePublished: post.date,
      dateModified: post.date,
      inLanguage: "pt-BR",
      articleSection: post.category,
      wordCount: post.readingMinutes * 200,
      timeRequired: `PT${post.readingMinutes}M`,
      author: {
        "@type": "Organization",
        name: post.author,
        url: SITE,
      },
      publisher: {
        "@type": "Organization",
        name: "Kentro Sistemas",
        url: SITE,
        logo: { "@type": "ImageObject", url: `${SITE}/og-image.png` },
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      keywords: post.keyword,
    };
    const breadcrumbLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Início", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE}/blog` },
        { "@type": "ListItem", position: 3, name: post.title, item: url },
      ],
    };
    const scripts: { type: string; children: string }[] = [
      { type: "application/ld+json", children: JSON.stringify(articleLd) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbLd) },
    ];
    if (post.faq?.length) {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: post.faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      });
    }
    return {
      meta: [
        { title: `${post.title} | Kentro` },
        { name: "description", content: post.description },
        { name: "author", content: post.author },
        { name: "keywords", content: post.keyword },
        { property: "og:type", content: "article" },
        { property: "og:title", content: post.title },
        { property: "og:description", content: post.description },
        { property: "og:url", content: url },
        { property: "og:image", content: articleImage },
        { property: "og:image:alt", content: post.coverAlt ?? post.title },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "article:published_time", content: post.date },
        { property: "article:modified_time", content: post.date },
        { property: "article:author", content: post.author },
        { property: "article:section", content: post.category },
        { property: "article:tag", content: post.keyword },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: post.title },
        { name: "twitter:description", content: post.description },
        { name: "twitter:image", content: articleImage },
        { name: "twitter:image:alt", content: post.coverAlt ?? post.title },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts,
    };
  },
  component: BlogPostPage,
  notFoundComponent: () => {
    const { slug } = Route.useParams();
    return <BlogNotFound slug={slug} locale="pt" />;
  },
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <BlogErrorState
        error={error instanceof Error ? error : new Error(String(error))}
        reset={() => {
          router.invalidate();
          reset();
        }}
        locale="pt"
      />
    );
  },
});

function BlogPostPage() {
  const { slug } = Route.useLoaderData();
  const post = POSTS_BY_SLUG[slug] as BlogPost;
  const related: BlogPost[] = (post.related ?? [])
    .map((s: string) => BLOG_POSTS.find((p) => p.slug === s))
    .filter((p): p is BlogPost => Boolean(p));

  const formattedDate = new Date(post.date).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  return (
    <article className="mx-auto max-w-3xl px-4 pt-24 pb-16 sm:px-6 lg:px-8">
      <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
        <Link to="/" className="hover:text-foreground">Início</Link>
        <ChevronRight className="size-3" />
        <Link to="/blog" className="hover:text-foreground">Blog</Link>
        <ChevronRight className="size-3" />
        <span className="text-foreground/80">{post.category}</span>
      </nav>

      <div className="mt-6 rounded-2xl border border-border bg-card/60 p-6 shadow-sm backdrop-blur-sm md:p-10">
        <header>
          <span className="inline-flex rounded-full border border-border bg-background/60 px-2.5 py-1 text-xs text-muted-foreground">
            {post.category}
          </span>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-foreground md:text-4xl lg:text-5xl">
            {post.title}
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">{post.description}</p>
          <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="size-4" /> {formattedDate}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="size-4" /> {post.readingMinutes} min de leitura
            </span>
            <span>Por {post.author}</span>
          </div>
        </header>

        <div className="prose mt-10 max-w-none text-foreground prose-headings:text-foreground prose-p:text-foreground/90 prose-strong:text-foreground prose-li:text-foreground/90 prose-a:text-primary-glow hover:prose-a:underline prose-blockquote:text-foreground/80 prose-blockquote:border-l-primary-glow prose-code:text-foreground prose-hr:border-border dark:prose-invert">
          {post.content}
        </div>
      </div>


      {post.faq?.length ? (
        <section className="mt-12">
          <h2 className="text-2xl font-bold tracking-tight md:text-3xl">Perguntas frequentes</h2>
          <div className="mt-5 space-y-3">
            {post.faq.map((f: { q: string; a: string }) => (
              <details
                key={f.q}
                className="group rounded-xl border border-border bg-card/40 p-4 open:bg-card/60"
              >
                <summary className="cursor-pointer list-none font-semibold">
                  {f.q}
                  <span className="float-right text-muted-foreground group-open:hidden">+</span>
                  <span className="float-right text-muted-foreground hidden group-open:inline">−</span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      ) : null}

      {post.internalLinks?.length ? (
        <section className="mt-10 rounded-2xl border border-border bg-card/40 p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Continue lendo
          </h2>
          <ul className="mt-3 space-y-1.5 text-sm">
            {post.internalLinks.map((l: { label: string; href: string }) => (
              <li key={l.href}>
                <a className="text-primary-glow hover:underline" href={l.href}>
                  {l.label} →
                </a>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="gradient-primary shadow-glow mt-12 rounded-2xl p-6 text-white md:p-8">
        <h2 className="text-xl font-bold md:text-2xl">Pronto para evoluir seu atendimento?</h2>
        <p className="mt-2 text-white/85">
          Fale com um especialista da Kentro e veja a plataforma rodando com seus próprios fluxos.
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <Link
            to="/contato"
            className="inline-flex items-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-semibold text-[color:var(--primary)] hover:bg-white/90"
          >
            Solicitar demonstração
          </Link>
          <a
            href={KENTRO.whatsappComercialUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-md border border-white/30 bg-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/20"
          >
            <MessageCircle className="size-4" /> WhatsApp (38) 3220-8706
          </a>
        </div>
      </section>

      {related.length ? (
        <section className="mt-14">
          <h2 className="text-2xl font-bold tracking-tight">Posts relacionados</h2>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            {related.map((r: BlogPost) => (
              <Link
                key={r.slug}
                to="/blog/$slug"
                params={{ slug: r.slug }}
                className="group rounded-xl border border-border bg-card/40 p-5 transition hover:border-primary-glow/40"
              >
                <span className="text-xs text-muted-foreground">{r.category}</span>
                <h3 className="mt-2 font-semibold group-hover:text-primary-glow">{r.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{r.description}</p>
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      <div className="mt-12">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="size-4" /> Voltar ao blog
        </Link>
      </div>
    </article>
  );
}
